import commonStrings from 'owa-strings/owa-mini-common-strings';
import React, { MouseEvent, useState } from 'react';
import safeOpenExternalLink from 'window-utils/lib/safeOpenExternalLink';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { ExtensionHeaderProps } from '../schema/ExtensionHeaderProps';
import { FeedbackForm } from 'mini-feedback';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { getModuleIndexUrl } from 'window-utils/lib/getModuleIndexUrl';
import { launchFeedback } from 'mini-sidepane-footer/lib/components/SidePaneFooter';
import { logTelemetry } from '../utils/logTelemetry';
import { logUsage } from 'owa-analytics';
import { MiniIcons } from 'owa-mini-icons';
import { Module } from 'mini-module-switcher';
import { shouldShowFeedbackLink } from 'mini-sidepane-footer/lib/utils/shouldShowFeedbackLink';

import {
    OPEN_IN_FULL_SCREEN_DP_NAME,
    RELOAD_CLICK_DP_NAME,
    FEEDBACK_CLICK_DP_NAME,
    CLOSE_CLICK_DP_NAME,
} from '../constants/telemetryConstants';

const styles = require('./ExtensionHeader.scss');
const classNames = require('classnames/bind').bind(styles);

// Open current page/URL in big OWA
const openInFullScreen = async (evt: MouseEvent<HTMLSpanElement>) => {
    evt.stopPropagation();
    safeOpenExternalLink(getModuleIndexUrl());
};

export default function ExtensionHeader({
    showPreview = false,
    hideFeedback = true,
    showReloadIcon,
    showOpenInFullScreenIcon,
    onReloadClick,
    onOpenInFullScreen,
    onFeedbackClick,
    onCloseClick,
    extensionHeaderStyles,
    showTitle = false,
}: ExtensionHeaderProps): JSX.Element {
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);

    const { containerStyle, feedbackStyle, iconStyle } = extensionHeaderStyles || {};
    const iconCommonStyles = classNames(styles.headerIcon, iconStyle);
    const closeIconClassNames = classNames(
        styles.closeIcon,
        iconCommonStyles,
        getIconClassName(MiniIcons.Cancel)
    );
    const refreshIconClassNames = classNames(
        styles.refreshIcon,
        iconCommonStyles,
        getIconClassName(MiniIcons.Refresh)
    );
    const expandIconClassNames = classNames(
        styles.expandIcon,
        iconCommonStyles,
        getIconClassName(MiniIcons.OpenInNewWindow)
    );
    const feedbackLinkClassNames = classNames(styles.feedback, feedbackStyle);

    const handleCloseIconClick = async (evt: MouseEvent<HTMLSpanElement>): Promise<void> => {
        // Log telemetry
        logTelemetry(CLOSE_CLICK_DP_NAME);

        if (onCloseClick) {
            await onCloseClick(evt);
            return;
        }

        // Default handling if the handler is not passed
        window?.close();
    };

    const handleReloadIconClick = async (evt: MouseEvent<HTMLSpanElement>): Promise<void> => {
        // Log telemetry
        logTelemetry(RELOAD_CLICK_DP_NAME);

        if (onReloadClick) {
            await onReloadClick(evt);
            return;
        }

        // Default handling if the handler is not passed
        window?.location?.reload();
    };

    const handleOpenInFullScreenIconClick = async (
        evt: MouseEvent<HTMLSpanElement>
    ): Promise<void> => {
        // Log telemetry
        logUsage(OPEN_IN_FULL_SCREEN_DP_NAME, undefined, { isCore: true });

        if (onOpenInFullScreen) {
            await onOpenInFullScreen(evt);
            return;
        }

        // Opens current URL in a new browser tab, by default if the handler is not passed.
        openInFullScreen(evt);
    };

    const handleFeedbackClick = (evt: MouseEvent<HTMLDivElement>): void => {
        // Log telemetry
        logTelemetry(FEEDBACK_CLICK_DP_NAME);
        launchFeedback(() => {
            if (onFeedbackClick) {
                onFeedbackClick(evt);
                return;
            }
            // Logic to show Feedback form lazily
            setShowFeedbackForm(true);
        });
    };

    const handleFeedbackCloseClick = () => setShowFeedbackForm(false);

    return (
        <div className={classNames(styles.headerContainer, containerStyle)}>
            {/* Header's left part */}
            <div className={styles.previewContainer}>
                {showPreview && <div className={styles.previewStyle}>Preview</div>}
                {!hideFeedback && shouldShowFeedbackLink() && (
                    <div
                        className={feedbackLinkClassNames}
                        onClick={handleFeedbackClick}
                        tabIndex={0}
                        role="button"
                        aria-label={commonStrings.sendFeedbackLabel}>
                        {commonStrings.sendFeedbackLabel}
                    </div>
                )}
                {showTitle && <h1 className={styles.title}>Outlook</h1>}
            </div>

            {/* Header's right part */}
            <div className={styles.rightContainer}>
                <i
                    role="button"
                    key="closeIcon"
                    title={commonStrings.closeOutlookHubIconTitle}
                    aria-label={commonStrings.closeOutlookHubIconTitle}
                    tabIndex={0}
                    className={closeIconClassNames}
                    onKeyPress={enterKeyHandler(handleCloseIconClick)}
                    onClick={handleCloseIconClick}
                />
                {showReloadIcon && (
                    <span
                        role="button"
                        key="refreshIcon"
                        title={commonStrings.refreshIconTitle}
                        aria-label={commonStrings.refreshIconTitle}
                        tabIndex={0}
                        className={refreshIconClassNames}
                        onClick={handleReloadIconClick}
                    />
                )}
                {showOpenInFullScreenIcon && (
                    <span
                        role="button"
                        key="expandIcon"
                        title={commonStrings.openInFullScreenTitle}
                        aria-label={commonStrings.openInFullScreenTitle}
                        tabIndex={0}
                        className={expandIconClassNames}
                        onClick={handleOpenInFullScreenIconClick}
                    />
                )}
            </div>

            {showFeedbackForm && (
                <FeedbackForm
                    containerModule={Module.Mail}
                    onCloseCallback={handleFeedbackCloseClick}
                />
            )}
        </div>
    );
}
